/**
 * DashboardFeed — conversational visual feed for AI-rendered panels.
 *
 * The dashboard is the home base. When the user types "show me
 * Blackburn's network" or "rank Senate Republicans on KOSA", the AI
 * fires `render_dashboard_panel` and a panel slides into THIS feed —
 * not the chat panel, not a navigation. The user stays oriented; the
 * dashboard accumulates around them.
 *
 * Persistence: panels live in the dashboard_feed_panels table on the
 * server (FB-MOX8K6IL). Closes the prior gap where panels were
 * localStorage-only and vanished on browser clear / didn't sync to a
 * second device. Re-fetched on workspace change + on BrowserBus
 * data_changed events with entity_type='dashboard_feed_panels'.
 *
 * Each panel is the visual result of a request:
 *   - title: the plain-English query the user typed
 *   - render_tool: name of a registered renderer (chart, member_grid,
 *     funnel, diff_panel, etc.)
 *   - render_input: the input shape that renderer expects
 *   - refresh_query: optional original query so "↻ refresh" can re-run
 *     the same AI request with fresh data
 *   - visibility: "private" (default) or "workspace" (visible to all
 *     workspace members)
 */

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { hasRenderer, RendererSlot } from "./renderers/index.js";
import { RotateCw, X } from "lucide-react";
import { DataUnavailable } from "./DataUnavailable.js";

interface DashboardPanel {
  id: number;
  panel_id: string;
  /** Tool name from the renderer registry — chart, member_grid, etc. */
  render_tool: string;
  /** Input that the renderer expects. */
  render_input: unknown;
  /** Headline shown in the panel header — typically the AI's restatement
   *  of the query ("Blackburn's Senate alignment", "Pro-Life ranking"). */
  title: string;
  /** Optional secondary line — usually a timestamp or scope. */
  subtitle?: string | null;
  /** Plain-English query that produced this panel — surfaces in the
   *  refresh tooltip + "↻ re-run" path. */
  refresh_query?: string | null;
  visibility: "private" | "workspace";
  created_by_user_id: string | null;
  chat_id: string | null;
  created_at: string;
}

// Legacy event names — kept exported for any in-flight callers, but
// the canonical path is now server-side persistence + BrowserBus
// data_changed invalidation. dispatchDashboardPanel becomes a no-op
// on the client; the source-of-truth move was to render_dashboard_panel
// writing to dashboard_feed_panels and the React fetch reflecting it.
export const DASHBOARD_FEED_EVENT = "cmcp:dashboard-feed-panel";
export const DASHBOARD_FEED_CLEAR_EVENT = "cmcp:dashboard-feed-clear";

export interface DashboardFeedProps {
  /** Workspace id — namespaces panel storage. Pass null/undefined to
   *  render nothing. */
  workspaceId?: string | null;
  /** Maximum number of panels rendered. Older panels stay in the DB but
   *  drop off the bottom of the visible list. Default 30. */
  maxPanels?: number;
  /** Optional CSRF header builder — mutations (dismiss, clear) hit
   *  csrfProtect-protected endpoints. CongressMCP wires getCsrfHeader. */
  csrfHeader?: () => Record<string, string>;
  /** Callback when the user clicks "↻ re-run" on a panel. If omitted
   *  (CongressMCP has no in-app chat to re-run it), the button is
   *  hidden. */
  onRefreshPanel?: (panel: DashboardPanel) => void;
  /** Optional title shown above the feed (e.g. "AI Brief"). */
  heading?: string;
  /** Optional empty-state shown when no panels yet. */
  emptyState?: React.ReactNode;
}

export function DashboardFeed({
  workspaceId,
  maxPanels = 30,
  csrfHeader,
  onRefreshPanel,
  heading,
  emptyState,
}: DashboardFeedProps) {
  const queryClient = useQueryClient();
  // Local optimistic-dismiss set so the UI doesn't flicker waiting for
  // the round trip + invalidation.
  const [optimisticDismissed, setOptimisticDismissed] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery<{ panels: DashboardPanel[] }>({
    queryKey: ["dashboard-feed-panels", workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId!)}/feed-panels?limit=${maxPanels}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to load dashboard panels");
      return res.json();
    },
    staleTime: 30_000,
  });

  // Reset optimistic-dismissed when workspace changes.
  useEffect(() => {
    setOptimisticDismissed(new Set());
    setSaveError(null);
  }, [workspaceId]);

  const panels = (data?.panels ?? []).filter((p) => !optimisticDismissed.has(p.panel_id));

  const dismissPanel = async (panelId: string) => {
    if (!workspaceId) return;
    setSaveError(null);
    setOptimisticDismissed((prev) => new Set(prev).add(panelId));
    try {
      const response = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/feed-panels/${encodeURIComponent(panelId)}/dismiss`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrfHeader?.() ?? {}),
          },
          body: "{}",
        }
      );
      if (!response.ok) throw new Error("Failed to dismiss panel");
      queryClient.invalidateQueries({ queryKey: ["dashboard-feed-panels", workspaceId] });
    } catch {
      // Roll back the optimistic dismiss if the request blew up — the
      // panel will reappear on next refetch.
      setOptimisticDismissed((prev) => {
        const next = new Set(prev);
        next.delete(panelId);
        return next;
      });
      setSaveError("The panel could not be dismissed. Try again.");
    }
  };

  const clearAll = async () => {
    if (!workspaceId) return;
    setSaveError(null);
    try {
      const response = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/feed-panels/clear`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrfHeader?.() ?? {}),
          },
          body: "{}",
        }
      );
      if (!response.ok) throw new Error("Failed to clear dashboard panels");
      queryClient.invalidateQueries({ queryKey: ["dashboard-feed-panels", workspaceId] });
    } catch {
      setSaveError("The feed could not be cleared. Try again.");
    }
  };

  const handleRefresh = (panel: DashboardPanel) => {
    onRefreshPanel?.(panel);
  };

  if (!workspaceId) return null;
  if (error) return <DataUnavailable message="Dashboard panels could not be loaded." retry={refetch} />;
  if (isLoading) return null; // first paint is invisible — avoids layout shift
  if (panels.length === 0) {
    return emptyState ? <div className="dashboard-feed-empty">{emptyState}</div> : null;
  }

  return (
    <div className="dashboard-feed flex flex-col gap-4">
      {saveError && <DataUnavailable message={saveError} retry={refetch} />}
      {heading && (
        <div className="flex items-center justify-between">
          <div className="card-head !mb-0">{heading}</div>
          {panels.length > 1 && (
            <button
              onClick={clearAll}
              className="text-[11px] text-stone-400 hover:text-stone-700 underline-offset-2 hover:underline"
              title="Dismiss your panels (other members' workspace-shared panels stay)"
            >
              clear my feed
            </button>
          )}
        </div>
      )}
      {panels.map((panel) => (
        <div
          key={panel.panel_id}
          className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm"
        >
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-stone-100 bg-stone-50/60">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-900 truncate flex items-center gap-1.5">
                {panel.title}
                {panel.visibility === "workspace" && (
                  <span
                    className="text-[9px] uppercase tracking-wider font-semibold bg-accent/60 text-ink px-1 py-0.5 rounded"
                    title="Visible to all workspace members"
                  >
                    shared
                  </span>
                )}
              </div>
              {panel.subtitle && (
                <div className="text-[11px] text-stone-500 mt-0.5 truncate">
                  {panel.subtitle}
                </div>
              )}
            </div>
            <div className="text-[10px] text-stone-400 font-mono tabular-nums">
              {new Date(panel.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </div>
            {onRefreshPanel && panel.refresh_query && (
              <button
                onClick={() => handleRefresh(panel)}
                className="w-6 h-6 rounded text-stone-400 hover:bg-stone-200 hover:text-stone-900 flex items-center justify-center"
                title={`Re-run: ${panel.refresh_query}`}
                aria-label="Re-run"
              >
                <RotateCw size={13} aria-hidden />
              </button>
            )}
            <button
              onClick={() => dismissPanel(panel.panel_id)}
              className="w-6 h-6 rounded text-stone-400 hover:bg-stone-200 hover:text-danger flex items-center justify-center"
              title="Dismiss"
              aria-label="Dismiss"
            >
              <X size={14} aria-hidden />
            </button>
          </div>
          <div className="p-3">
            {hasRenderer(panel.render_tool) ? (
              <RendererSlot tool={panel.render_tool} input={panel.render_input} />
            ) : (
              <div className="text-xs italic text-stone-400">
                Renderer not registered: {panel.render_tool}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Legacy compat shim — these fired window CustomEvents that the prior
 * DashboardFeed listened for. Now that panels are server-persisted +
 * fetched on demand, dispatching the events is a no-op (the canonical
 * path is render_dashboard_panel writing to dashboard_feed_panels +
 * BrowserBus data_changed → tanstack-query invalidation). Kept exported
 * so any in-flight callers don't break at import time.
 */
export function dispatchDashboardPanel(_detail: unknown): void {
  /* no-op — server-side persistence supersedes the local event path */
}

export function dispatchDashboardClear(): void {
  /* no-op — see dispatchDashboardPanel */
}
